import DatasetTitle from "components/basic/display-titles/DatasetTitle";
import {DatasetDropdown} from "components/basic/dropdowns/dataset/DatasetDropdown";
import {IDatasetSetpointConfig} from "components/dashboard/dataset-setpoint-table/common/api/interfaces/interfaces";
import {Button} from "primereact/button";
import {ColorPicker} from "primereact/colorpicker";
import {Column} from "primereact/column";
import {DataTable} from "primereact/datatable";
import {Dialog} from "primereact/dialog";
import {InputText} from "primereact/inputtext";
import {ProgressSpinner} from "primereact/progressspinner";
import React, {lazy, Suspense, useState} from "react";
import { handleRowReorder } from "utils/utils";

const ValuesValidationEditor = lazy(() => import("../values-validation-editor/ValuesValidationEditor"));

interface IDatasetSetpointValuesEditorProps {
    currentConfig: IDatasetSetpointConfig;
    setCurrentConfig: (config: IDatasetSetpointConfig) => void;
}

enum DefaultHighlightColor {
    defaultTextColor = "#000000",
    defaultBackgroundColor = "#FFFFFF",
}

const DatasetSetpointValuesEditor = ({currentConfig, setCurrentConfig}: IDatasetSetpointValuesEditorProps) => {
    const indexedValues = structuredClone(currentConfig).values.map((item, index) => ({...item, index}));

    const [isValidationDialogVisible, setValidationDialogVisibility] = useState<boolean>(false);
    const [selectedValue, setSelectedValue] = useState<any>(undefined);
    const [editingRow, setEditingRow] = useState<any>(null);

    const handleShowValidationsClicked = (rowData: any) => {
        setSelectedValue(rowData);
        setValidationDialogVisibility(true);
    };

    const defaultHighlightBodyTemplate = (rowData: any) => {
        if (rowData.name === " ") {
            return <span></span>;
        }
        if (editingRow && editingRow.index === rowData.index) {
            return (
                <div className={"flex justify-content-center align-items-center"}>
                    <ColorPicker
                        value={editingRow.highlightColor?.color || DefaultHighlightColor.defaultTextColor}
                        onChange={(e) =>
                            setEditingRow({
                                ...editingRow,
                                highlightColor: {
                                    ...editingRow.highlightColor,
                                    color: "#" + e.value,
                                },
                            })
                        }
                    />
                    <ColorPicker
                        value={editingRow.highlightColor?.bgColor || DefaultHighlightColor.defaultBackgroundColor}
                        onChange={(e) =>
                            setEditingRow({
                                ...editingRow,
                                highlightColor: {
                                    ...editingRow.highlightColor,
                                    bgColor: "#" + e.value,
                                },
                            })
                        }
                    />
                    <i
                        className="pi pi-refresh ml-2 cursor-pointer"
                        onClick={() =>
                            setEditingRow({
                                ...editingRow,
                                highlightColor: {
                                    color: DefaultHighlightColor.defaultTextColor,
                                    bgColor: DefaultHighlightColor.defaultBackgroundColor,
                                },
                            })
                        }
                    />
                </div>
            );
        } else {
            let tempColor = rowData.highlightColor ? rowData.highlightColor?.color : DefaultHighlightColor.defaultTextColor;
            let tempBgColor = rowData.highlightColor
                ? rowData.highlightColor?.bgColor
                : DefaultHighlightColor.defaultBackgroundColor;
            tempColor = tempColor.startsWith("#") ? tempColor : "#" + tempColor;
            tempBgColor = tempBgColor.startsWith("#") ? tempBgColor : "#" + tempBgColor;
            return (
                <span className="p-1 pl-2 pr-2" style={{color: tempColor, backgroundColor: tempBgColor}}>
                Text
            </span>
            );
        }
    };

    const validationBodyTemplate = (rowData: any) => {
        if (rowData.name === " ") {
            return <span></span>;
        }
        return (
            <Button
                label="Show"
                className="p-button-sm p-button-text p-button-outlined"
                onClick={() => handleShowValidationsClicked(rowData)}
                disabled={
                    rowData.dataset === undefined ||
                    rowData.dataset.length === 0 ||
                    rowData.setpointDataset === undefined ||
                    rowData.setpointDataset.length === 0
                }
            />
        );
    };

    const datasetBodyTemplate = (rowData: any) => {
        if (editingRow && editingRow.index === rowData.index) {
            return (
                <DatasetDropdown
                    value={editingRow.dataset}
                    onChange={(e) => {
                        if (e === null) {
                            return;
                        } else {
                            const updatedRow = {...editingRow, dataset: e};
                            setEditingRow(updatedRow);
                        }

                    }}
                />
            );
        } else {
            if (rowData.dataset === undefined || rowData.dataset.length === 0) {
                return <span></span>;
            }
            return <DatasetTitle identifier={rowData.dataset}/>;
        }
    };

    const setpointDatasetBodyTemplate = (rowData: any) => {

        if (editingRow && editingRow.index === rowData.index) {
            return (
                <DatasetDropdown
                    value={editingRow.setpointDataset}
                    onChange={(e) => {
                        const updatedRow = {...editingRow, setpointDataset: e};
                        setEditingRow(updatedRow);
                    }}
                />
            );
        } else {
            if (rowData.setpointDataset === undefined || rowData.setpointDataset.length === 0) {
                return <span></span>;
            }
            return <DatasetTitle identifier={rowData.setpointDataset}/>;
        }
    };

    const deleteValueButtonBodyTemplate = (rowData: any) => {
        return (
            <i
                className="pi pi-trash text-red-500 cursor-pointer"
                onClick={() => {
                    const tempConfig = structuredClone(currentConfig);
                    tempConfig.values.splice(rowData.index, 1);
                    setCurrentConfig(tempConfig);
                }}
            />
        );
    };

    const handleAddValue = () => {
        const tempConfig = structuredClone(currentConfig);
        const newValue = {dataset: "", setpointDataset: "", name: "", tag: ""};
        tempConfig.values.push(newValue);
        setCurrentConfig(tempConfig);
    };

    const handleAddSeparator = () => {
        const tempConfig = structuredClone(currentConfig);
        const newValue = {name: " "};
        tempConfig.values.push(newValue);
        setCurrentConfig(tempConfig);
    };

    const onRowEditInit = (rowData: any) => {
        setEditingRow(rowData);
    };

    const onRowEditSave = () => {
        if (editingRow) {
            const updatedValues = [...currentConfig.values];
            updatedValues[editingRow.index] = {...editingRow};
            setCurrentConfig({...currentConfig, values: updatedValues});
            setEditingRow(null);
        }
    };


    const tableHeader = (
        <div className="flex justify-content-end gap-1">
            <Button label="Add values" icon="pi pi-plus" className="p-button-success" onClick={handleAddValue}/>
            <Button label="Add separator" icon="pi pi-plus" className="p-button-info" onClick={handleAddSeparator}/>
        </div>
    );

    const rowEditorTemplate = (rowData: any) => {
        if (rowData.name === " ") {
            return <span></span>;
        }

        if (editingRow && editingRow.index === rowData.index) {
            return (
                <>
                    <Button
                        icon="pi pi-check"
                        className="p-button-rounded p-button-success p-button-text"
                        onClick={onRowEditSave}
                    />
                    <Button
                        icon="pi pi-times"
                        className="p-button-rounded p-button-danger p-button-text"
                        onClick={() => setEditingRow(null)}
                    />
                </>
            );
        } else {
            return (
                <Button
                    icon="pi pi-pencil"
                    className="p-button-rounded p-button-text"
                    onClick={() => onRowEditInit(rowData)}
                />
            );
        }
    };

    const tagBodyTemplate = (rowData: any) => {
        if (editingRow && editingRow.index === rowData.index) {
            return (
                <InputText
                    value={editingRow.tag}
                    className="w-full"
                    onChange={(e) => {
                        const updatedRow = {...editingRow, tag: e.target.value};
                        setEditingRow(updatedRow);
                    }}
                />
            );
        } else {
            return <span>{rowData.tag}</span>;
        }
    };

    const nameBodyTemplate = (rowData: any) => {
        if (editingRow && editingRow.index === rowData.index) {
            return (
                <InputText
                    value={editingRow.name}
                    className="w-full"
                    onChange={(e) => {
                        const updatedRow = {...editingRow, name: e.target.value};
                        setEditingRow(updatedRow);
                    }}
                />
            );
        } else {
            return <span>{rowData.name}</span>;
        }
    };

    return (
        <>
            <DataTable
                value={indexedValues}
                editMode="row"
                onRowEditInit={onRowEditInit}
                onRowEditSave={onRowEditSave}
                editingRows={editingRow ? [editingRow] : []}
                tableStyle={{width: "100%"}}
                onRowReorder={handleRowReorder(currentConfig.values, setCurrentConfig, currentConfig)}
                size="small"
                reorderableRows
                header={tableHeader}
            >
                <Column rowReorder style={{width: "3%"}}/>
                <Column field="tag" header="Tag" style={{width: "5%"}} body={tagBodyTemplate}/>
                <Column
                    field="dataset"
                    header="Dataset"
                    style={{width: "20%"}}
                    body={datasetBodyTemplate}
                />
                <Column
                    field="name"
                    header="Dataset Name"
                    style={{width: "20%"}}
                    body={nameBodyTemplate}
                />
                <Column
                    field="setpointDataset"
                    header="Setpoint dataset"
                    style={{width: "20%"}}
                    body={setpointDatasetBodyTemplate}
                />
                <Column
                    field="highlightColor"
                    header="Default Highlight"
                    body={defaultHighlightBodyTemplate}
                    align="center"
                    style={{width: "10%"}}
                />
                <Column
                    field="validation"
                    header="Validation"
                    align="center"
                    style={{width: "5%"}}
                    body={validationBodyTemplate}
                />
                <Column body={rowEditorTemplate} style={{width: "5%"}} align="center"/>
                <Column style={{width: "2%"}} align="center" body={deleteValueButtonBodyTemplate}/>
            </DataTable>
            <Dialog
                header="Validation"
                visible={isValidationDialogVisible}
                style={{width: "30vw"}}
                onHide={() => setValidationDialogVisibility(false)}
            >
                <Suspense fallback={<ProgressSpinner/>}>
                    <ValuesValidationEditor
                        selectedValue={selectedValue}
                        currentConfig={currentConfig}
                        setCurrentConfig={setCurrentConfig}
                    />
                </Suspense>
            </Dialog>
        </>
    );
};

export default DatasetSetpointValuesEditor;
